// /Users/pramod/Desktop/school-erp/frontend/src/Tenant-Admin/WebsiteManager/sections/EventsSection.js

import React, { useState, useEffect } from 'react';
import { FiSave, FiPlus, FiTrash2, FiX, FiImage } from 'react-icons/fi';
import ImagePreview from '../common/ImagePreview';
import api from '../../../api';

const EventsSection = ({ websiteData, handleInputChange, handleSave, tenantId }) => {
  const events = websiteData?.events || { items: [] };

  // Local state vectors to completely isolate text fields during live input typing
  const [localTitle, setLocalTitle] = useState(events.sectionTitle || '');
  const [localSubtitle, setLocalSubtitle] = useState(events.sectionSubtitle || '');
  const [localDisplayStyle, setLocalDisplayStyle] = useState(events.displayStyle || 'card');
  const [localCheckboxes, setLocalCheckboxes] = useState({
    showSpeakers: events.showSpeakers !== false,
    showVenue: events.showVenue !== false,
    showTime: events.showTime !== false
  });
  const [localItems, setLocalItems] = useState(events.items || []);

  const [newItem, setNewItem] = useState({
    title: '',
    description: '',
    date: '',
    time: '',
    venue: '',
    speakers: [],
    registrationLink: '',
    isFree: true
  });
  const [uploading, setUploading] = useState({});
  const [saving, setSaving] = useState(false);

  // Sync isolated fields values back from props only on initial load or switch of tenant parameters
  useEffect(() => {
    console.log('[EventsSection] Synchronizing isolated parameters from core source props data baseline.');
    const sec = websiteData?.events || {};
    setLocalTitle(sec.sectionTitle || '');
    setLocalSubtitle(sec.sectionSubtitle || '');
    setLocalDisplayStyle(sec.displayStyle || 'card');
    setLocalCheckboxes({
      showSpeakers: sec.showSpeakers !== false,
      showVenue: sec.showVenue !== false,
      showTime: sec.showTime !== false
    });
    setLocalItems(sec.items || []);
  }, [websiteData?.events, tenantId]);

  const handleToggle = () => {
    handleInputChange('events', 'isActive', !events.isActive);
  };

  const handleGlobalFieldChange = (field, value) => {
    console.log(`[EventsSection] Setting isolated configuration field: Key [${field}] -> Value [${value}]`);
    if (field === 'sectionTitle') setLocalTitle(value);
    if (field === 'sectionSubtitle') setLocalSubtitle(value);
    if (field === 'displayStyle') setLocalDisplayStyle(value);
  };

  const handleLocalCheckboxToggle = (key) => {
    setLocalCheckboxes(prev => {
      const nextState = { ...prev, [key]: !prev[key] };
      console.log(`[EventsSection] Updating active visibility flags [${key}]:`, nextState[key]);
      return nextState;
    });
  };

  const handleAddItem = () => {
    if (!newItem.title || !newItem.date) return;
    
    const item = {
      _id: Date.now().toString(),
      title: newItem.title,
      description: newItem.description,
      date: newItem.date,
      time: newItem.time,
      venue: newItem.venue,
      speakers: newItem.speakers,
      registrationLink: newItem.registrationLink,
      isFree: newItem.isFree,
      isFeatured: false,
      image: ''
    };
    
    const updatedItems = [...localItems, item];
    console.log('[EventsSection] Appending a new calendar layout card locally:', item);
    setLocalItems(updatedItems);
    
    // Sync array adjustments instantly up to retain mapping collection sequence logic
    handleInputChange('events', 'items', updatedItems);
    setNewItem({
      title: '',
      description: '',
      date: '',
      time: '',
      venue: '',
      speakers: [],
      registrationLink: '',
      isFree: true
    });
  };

  const handleRemoveItem = (index) => {
    const updatedItems = localItems.filter((_, i) => i !== index);
    console.log('[EventsSection] Extracting calendar layout row event index:', index);
    setLocalItems(updatedItems);
    
    // Sync array adjustments instantly up to retain mapping collection sequence logic
    handleInputChange('events', 'items', updatedItems);
  };

  const handleItemChange = (index, field, value) => {
    const updatedItems = [...localItems];
    updatedItems[index] = { ...updatedItems[index], [field]: value };
    
    console.log(`[EventsSection] Modifying card properties locally row [${index}] -> Key [${field}]`);
    setLocalItems(updatedItems);
  };

  const handleImageUpload = async (event, index) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (file.size > 2 * 1024 * 1024) {
      alert('Image size must be less than 2MB');
      return;
    }

    const localBlobPreview = URL.createObjectURL(file);
    const previewItems = [...localItems];
    previewItems[index] = { ...previewItems[index], image: localBlobPreview };
    setLocalItems(previewItems);

    setUploading(prev => ({ ...prev, [index]: true }));

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('section', 'events');
      formData.append('itemIndex', index);

      const response = await api.post(`/api/website/${tenantId}/upload/events`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      if (response.data.success) {
        const fileUrl = response.data.data?.url || response.data.data;
        console.log('[EventsSection] Remote host server image uploaded successfully path location URL:', fileUrl);
        const updatedItems = [...localItems];
        updatedItems[index] = { ...updatedItems[index], image: fileUrl };
        setLocalItems(updatedItems);
        handleInputChange('events', 'items', updatedItems);
      }
    } catch (error) {
      console.error('Upload error:', error);
      alert('Failed to upload image');
    } finally {
      setUploading(prev => ({ ...prev, [index]: false }));
      event.target.value = '';
    }
  };

  const resolveAssetUrl = (urlPath) => {
    if (!urlPath) return '';
    if (typeof urlPath === 'string' && (urlPath.includes('tenants%2Fnull') || urlPath.includes('tenants/null'))) {
      return '';
    }
    const base = (api?.defaults?.baseURL || '').replace(/\/+$/, '') || (process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '');
    if (typeof urlPath === 'object') {
      if (urlPath.url) return urlPath.url.startsWith('/') ? (base ? `${base}${urlPath.url}` : urlPath.url) : urlPath.url;
      if (urlPath.fileId) return base ? `${base}/api/files/tenant-website/${urlPath.fileId}` : `/api/files/tenant-website/${urlPath.fileId}`;
      return '';
    }
    if (/^https?:\/\//i.test(urlPath)) return urlPath;
    if (urlPath.startsWith('/')) return base ? `${base}${urlPath}` : urlPath;
    return urlPath;
  };

  const handleRemoveImage = (index) => {
    const updatedItems = [...localItems];
    updatedItems[index] = { ...updatedItems[index], image: '' };
    setLocalItems(updatedItems);
    handleInputChange('events', 'items', updatedItems);
  };

  const handleSaveClick = async () => {
    setSaving(true);
    console.log('[EventsSection] Exporting all decoupled parameters to update the main state configurations tree.');
    
    try {
      // Synchronously push local parameter properties up into global layout tree data
      await handleInputChange('events', 'sectionTitle', localTitle);
      await handleInputChange('events', 'sectionSubtitle', localSubtitle);
      await handleInputChange('events', 'displayStyle', localDisplayStyle);
      await handleInputChange('events', 'showSpeakers', localCheckboxes.showSpeakers);
      await handleInputChange('events', 'showVenue', localCheckboxes.showVenue);
      await handleInputChange('events', 'showTime', localCheckboxes.showTime);
      await handleInputChange('events', 'items', localItems);

      const assembledPayload = {
        ...events,
        sectionTitle: localTitle,
        sectionSubtitle: localSubtitle,
        displayStyle: localDisplayStyle,
        showSpeakers: localCheckboxes.showSpeakers,
        showVenue: localCheckboxes.showVenue,
        showTime: localCheckboxes.showTime,
        items: localItems
      };

      await handleSave('events', assembledPayload);
    } catch (error) {
      console.error('Failed to save events:', error);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mt-4 mx-2 sm:mx-0 bg-slate-50 rounded-2xl border border-slate-200/80 shadow-sm overflow-hidden">
      
      {/* Top Header Card Action Area */}
      <div className="bg-white p-4 sm:p-6 border-b border-slate-200">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h2 className="text-xl font-bold text-slate-900 tracking-tight">Events</h2>
            <p className="text-sm text-slate-500 mt-0.5">Manage upcoming events, calendar milestones and webinars</p>
          </div>
          
          <div className="grid grid-cols-2 gap-2 sm:flex sm:items-center sm:space-x-3">
            <button
              type="button"
              onClick={handleToggle}
              className={`flex items-center justify-center px-4 py-2.5 rounded-xl text-sm font-semibold transition-all ${
                events.isActive !== false
                  ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                  : 'bg-slate-100 text-slate-600 border border-slate-200'
              }`}
            >
              {events.isActive !== false ? (
                <>
                  <span className="w-2 h-2 rounded-full bg-emerald-500 mr-2" />
                  Active
                </>
              ) : (
                <>
                  <span className="w-2 h-2 rounded-full bg-slate-400 mr-2" />
                  Inactive
                </>
              )}
            </button>

            <button
              type="button"
              onClick={handleSaveClick}
              disabled={saving}
              className={`flex items-center justify-center px-5 py-2.5 rounded-xl text-sm font-semibold shadow-sm transition-all ${
                saving 
                  ? 'bg-slate-300 text-slate-500 cursor-not-allowed'
                  : 'bg-blue-600 text-white hover:bg-blue-700 active:scale-[0.98]'
              }`}
            >
              <FiSave className="mr-2 h-4 w-4" />
              {saving ? 'Saving...' : 'Save All'}
            </button>
          </div>
        </div>
      </div>

      <div className="p-4 sm:p-6 space-y-6">
        
        {/* Core Layout Inputs settings block card */}
        <div className="bg-white rounded-xl p-4 sm:p-5 border border-slate-200/80 shadow-sm space-y-4">
          <h3 className="text-xs font-bold uppercase tracking-wider text-slate-400">Section Settings</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1.5">Section Title</label>
              <input
                type="text"
                value={localTitle}
                onChange={(e) => handleGlobalFieldChange('sectionTitle', e.target.value)}
                className="w-full px-3 py-2.5 text-sm border border-slate-200 rounded-xl bg-slate-50/50 focus:bg-white focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all outline-none"
                placeholder="Upcoming Events"
              />
            </div>

            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1.5">Section Subtitle</label>
              <input
                type="text"
                value={localSubtitle}
                onChange={(e) => handleGlobalFieldChange('sectionSubtitle', e.target.value)}
                className="w-full px-3 py-2.5 text-sm border border-slate-200 rounded-xl bg-slate-50/50 focus:bg-white focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all outline-none"
                placeholder="Join us for these exciting events"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-2 border-t border-slate-100 items-end">
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1.5">Display Layout Alignment</label>
              <select
                value={localDisplayStyle}
                onChange={(e) => handleGlobalFieldChange('displayStyle', e.target.value)}
                className="w-full px-3 py-2.5 text-sm border border-slate-200 rounded-xl bg-slate-50/50 focus:bg-white focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all outline-none appearance-none"
              >
                <option value="card">Grid Block Cards</option>
                <option value="list">Vertical Row List</option>
              </select>
            </div>

            <div className="md:col-span-2 bg-slate-50 rounded-xl p-3 border border-slate-150 flex flex-wrap gap-4">
              {[
                { label: 'Show Speakers', key: 'showSpeakers', checked: localCheckboxes.showSpeakers },
                { label: 'Show Venue', key: 'showVenue', checked: localCheckboxes.showVenue },
                { label: 'Show Time', key: 'showTime', checked: localCheckboxes.showTime },
              ].map((checkbox) => (
                <label key={checkbox.key} className="flex items-center space-x-2.5 cursor-pointer group select-none">
                  <input
                    type="checkbox"
                    checked={checkbox.checked}
                    onChange={() => handleLocalCheckboxToggle(checkbox.key)}
                    className="sr-only"
                  />
                  <div className={`w-5 h-5 rounded-md flex items-center justify-center transition-all border ${
                    checkbox.checked 
                      ? 'bg-blue-600 border-blue-600 text-white' 
                      : 'bg-white border-slate-300 text-transparent group-hover:border-slate-400'
                  }`}>
                    <span className={`w-2 h-2 rounded-sm bg-white ${checkbox.checked ? 'block' : 'hidden'}`} />
                  </div>
                  <span className="text-xs font-medium text-slate-600 group-hover:text-slate-900 transition-colors">
                    {checkbox.label}
                  </span>
                </label>
              ))}
            </div>
          </div>
        </div>

        {/* Existing Added Event List layout blocks mapping */}
        <div className="space-y-4">
          <div className="flex items-center justify-between px-1">
            <h3 className="text-sm font-bold text-slate-800">Events</h3>
            <span className="text-xs font-semibold bg-slate-200 text-slate-600 px-2.5 py-0.5 rounded-full">
              {localItems.length} Events Added
            </span>
          </div>

          <div className="space-y-4">
            {localItems.map((item, index) => {
              const resolvedImage = resolveAssetUrl(item.image);

              return (
                <div key={item._id || index} className="bg-white border border-slate-200 rounded-xl shadow-sm hover:shadow-md transition-all overflow-hidden">
                  <div className="p-4 sm:p-5 space-y-4">
                    
                    {/* Input form details configurations maps alignment fields */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div className="col-span-1 sm:col-span-2 space-y-1">
                        <span className="text-[10px] font-bold uppercase text-slate-400">Event Headline Title</span>
                        <input
                          type="text"
                          value={item.title || ''}
                          onChange={(e) => handleItemChange(index, 'title', e.target.value)}
                          placeholder="Event Title"
                          className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg outline-none bg-slate-50/30 focus:bg-white focus:border-blue-500"
                        />
                      </div>

                      <div className="space-y-1">
                        <span className="text-[10px] font-bold uppercase text-slate-400">Scheduled Date</span>
                        <input
                          type="date"
                          value={item.date || ''}
                          onChange={(e) => handleItemChange(index, 'date', e.target.value)}
                          className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg outline-none bg-slate-50/30 focus:bg-white focus:border-blue-500"
                        />
                      </div>

                      <div className="space-y-1">
                        <span className="text-[10px] font-bold uppercase text-slate-400">Scheduled Time</span>
                        <input
                          type="text"
                          value={item.time || ''}
                          onChange={(e) => handleItemChange(index, 'time', e.target.value)}
                          placeholder="e.g., 10:00 AM - 1:00 PM"
                          className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg outline-none bg-slate-50/30 focus:bg-white focus:border-blue-500"
                        />
                      </div>

                      <div className="col-span-1 sm:col-span-2 space-y-1">
                        <span className="text-[10px] font-bold uppercase text-slate-400">Venue Location Address</span>
                        <input
                          type="text"
                          value={item.venue || ''}
                          onChange={(e) => handleItemChange(index, 'venue', e.target.value)}
                          placeholder="Auditorium, Seminar Hall, Online Zoom etc..."
                          className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg outline-none bg-slate-50/30 focus:bg-white focus:border-blue-500"
                        />
                      </div>

                      <div className="col-span-1 sm:col-span-2 space-y-1">
                        <span className="text-[10px] font-bold uppercase text-slate-400">Short Event Description</span>
                        <textarea
                          value={item.description || ''}
                          onChange={(e) => handleItemChange(index, 'description', e.target.value)}
                          placeholder="Short event overview summary text details..."
                          rows="2"
                          className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg outline-none bg-slate-50/30 focus:bg-white focus:border-blue-500 resize-y"
                        />
                      </div>

                      <div className="col-span-1 sm:col-span-2 space-y-1">
                        <span className="text-[10px] font-bold uppercase text-slate-400">Invited Speakers & Panelists Names (comma split)</span>
                        <input
                          type="text"
                          value={item.speakers?.join(', ') || ''}
                          onChange={(e) => handleItemChange(index, 'speakers', e.target.value.split(',').map(s => s.trim()).filter(Boolean))}
                          placeholder="e.g., Dr. John Doe, Prof. Alan Turing"
                          className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg outline-none bg-slate-50/30 focus:bg-white focus:border-blue-500"
                        />
                      </div>
                    </div>

                    {/* Integrated Custom Media file upload handler box wrapper component */}
                    <div className="flex flex-col sm:flex-row sm:items-center gap-4 bg-slate-50 p-3 rounded-xl border border-slate-150">
                      <div className="flex-shrink-0 mx-auto sm:mx-0">
                        <div className="w-16 h-16 rounded-xl border border-slate-200 flex items-center justify-center overflow-hidden bg-white shadow-inner relative group">
                          {resolvedImage ? (
                            <div className="w-full h-full relative">
                              <ImagePreview src={resolvedImage} alt={item.title || "Event Image Preview"} className="w-full h-full object-cover" />
                              <button
                                type="button"
                                onClick={() => handleRemoveImage(index)}
                                className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity text-white rounded-xl"
                              >
                                <FiX className="h-4 w-4" />
                              </button>
                            </div>
                          ) : (
                            <FiImage className="h-6 w-6 text-slate-400" />
                          )}
                        </div>
                      </div>
                      <div className="text-center sm:text-left flex-1">
                        <input
                          type="file"
                          id={`event-image-${index}`}
                          accept="image/jpeg,image/png,image/webp"
                          onChange={(e) => handleImageUpload(e, index)}
                          disabled={uploading[index]}
                          className="hidden"
                        />
                        <label
                          htmlFor={`event-image-${index}`}
                          className={`inline-flex items-center px-3 py-1.5 border border-slate-200 rounded-lg text-xs font-semibold text-slate-700 bg-white hover:bg-slate-50 shadow-sm cursor-pointer transition-all select-none ${
                            uploading[index] ? 'opacity-50 cursor-not-allowed' : 'active:scale-95'
                          }`}
                        >
                          {uploading[index] ? 'Uploading...' : (resolvedImage ? 'Change Image Backdrop' : 'Upload Image Backdrop')}
                        </label>
                        <p className="text-[10px] text-slate-400 mt-1">Accepts JPG, PNG, WEBP under 2MB</p>
                      </div>
                    </div>

                    <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between pt-2 border-t border-slate-100">
                      <div className="flex items-center space-x-5 select-none">
                        <label className="flex items-center space-x-2 cursor-pointer group">
                          <input
                            type="checkbox"
                            checked={item.isFree !== false}
                            onChange={(e) => handleItemChange(index, 'isFree', e.target.checked)}
                            className="sr-only"
                          />
                          <div className={`w-4 h-4 rounded border transition-all flex items-center justify-center ${
                            item.isFree !== false ? 'bg-blue-600 border-blue-600 text-white' : 'border-slate-300 bg-white'
                          }`}>
                            <span className={`w-1.5 h-1.5 rounded-xs bg-white ${item.isFree !== false ? 'block' : 'hidden'}`} />
                          </div>
                          <span className="text-xs font-semibold text-slate-600 group-hover:text-slate-800">Free Entrance Event</span>
                        </label>

                        <label className="flex items-center space-x-2 cursor-pointer group">
                          <input
                            type="checkbox"
                            checked={item.isFeatured || false}
                            onChange={(e) => handleItemChange(index, 'isFeatured', e.target.checked)}
                            className="sr-only"
                          />
                          <div className={`w-4 h-4 rounded border transition-all flex items-center justify-center ${
                            item.isFeatured ? 'bg-blue-600 border-blue-600 text-white' : 'border-slate-300 bg-white'
                          }`}>
                            <span className={`w-1.5 h-1.5 rounded-xs bg-white ${item.isFeatured ? 'block' : 'hidden'}`} />
                          </div>
                          <span className="text-xs font-semibold text-slate-600 group-hover:text-slate-800">Featured Highlight Card</span>
                        </label>
                      </div>

                      <button
                        type="button"
                        onClick={() => handleRemoveItem(index)}
                        className="text-rose-500 hover:text-rose-700 font-semibold text-xs flex items-center px-2 py-1 rounded-md hover:bg-rose-50 transition-colors self-end sm:self-auto"
                      >
                        <FiTrash2 className="mr-1 h-3.5 w-3.5" />
                        Delete Event
                      </button>
                    </div>

                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Add Entry Action Sub-form Block layouts */}
        <div className="bg-white border-2 border-dashed border-slate-200 rounded-xl p-4 sm:p-5 shadow-sm">
          <div className="flex items-center space-x-2 mb-3">
            <div className="p-1 bg-blue-50 rounded-md text-blue-600">
              <FiPlus className="h-4 w-4 stroke-[3]" />
            </div>
            <h4 className="text-sm font-bold text-slate-800">Create New Event Entry</h4>
          </div>

          <div className="space-y-3">
            <input
              type="text"
              value={newItem.title}
              onChange={(e) => setNewItem({ ...newItem, title: e.target.value })}
              placeholder="Event Title *"
              className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg outline-none focus:border-blue-500"
            />
            
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <input
                type="date"
                value={newItem.date}
                onChange={(e) => setNewItem({ ...newItem, date: e.target.value })}
                className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg outline-none focus:border-blue-500"
              />
              <input
                type="text"
                value={newItem.time}
                onChange={(e) => setNewItem({ ...newItem, time: e.target.value })}
                placeholder="Time (e.g., 10:00 AM - 1:00 PM)"
                className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg outline-none focus:border-blue-500"
              />
            </div>
            
            <div className="flex flex-col sm:flex-row items-end gap-3">
              <textarea
                value={newItem.description}
                onChange={(e) => setNewItem({ ...newItem, description: e.target.value })}
                placeholder="Short event overview summary text content details... *"
                rows="2"
                className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg outline-none focus:border-blue-500 resize-y"
              />
              <button
                type="button"
                onClick={handleAddItem}
                className="w-full sm:w-auto inline-flex items-center justify-center px-5 py-2.5 bg-slate-900 text-white font-semibold rounded-lg text-sm hover:bg-slate-800 transition-all active:scale-[0.98] shadow-sm whitespace-nowrap"
              >
                Insert Event
              </button>
            </div>
          </div>
        </div>

      </div>
    </div>
  );
};

export default EventsSection;